import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Tag, X, Loader2, Utensils, Check, ChefHat, ArrowUp, ArrowDown, AlertTriangle, EyeOff, DollarSign, CheckCircle, Layers, Save, GripVertical, Search, ArrowLeft, ChevronUp, ChevronDown, Eye } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { SearchBar } from '../components/ui/SearchBar';
import { FilterDropdown } from '../components/ui/FilterDropdown';
import { MenuItemFormModal } from '../components/MenuItemFormModal';
import { useActivityLog } from '../contexts/ActivityLogContext';
import { useToast } from '../components/ui/Toast';
import { supabaseClient, isSupabaseConfigured } from '../lib/supabase';
import { getSupabase } from '../lib/supabase';
import { useDocumentTitle } from '../utils/useDocumentTitle';
import { useHasPermission } from '../utils/permissions';
export function MenuView() {
  useDocumentTitle('Menú');
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<any>(null);
  const [sortBy, setSortBy] = useState('name');
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState('');
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [categoryToDelete, setCategoryToDelete] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingCategoryIndex, setEditingCategoryIndex] = useState<number | null>(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  // Template states
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [expandedTemplateId, setExpandedTemplateId] = useState<string | null>(null);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [applyingTemplate, setApplyingTemplate] = useState<any | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [searchItemTerm, setSearchItemTerm] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const {
    addLog
  } = useActivityLog();
  const {
    showToast
  } = useToast();
  // Permission checks
  const canCreate = useHasPermission('menu.create');
  const canEdit = useHasPermission('menu.edit');
  const canDelete = useHasPermission('menu.delete');
  const canManageCategories = useHasPermission('menu.manageCategories');
  // Helper: persist category order to both localStorage AND Supabase settings
  const saveCategoryOrder = useCallback(async (orderedCategories: string[]) => {
    // Always save to localStorage (instant, reliable)
    localStorage.setItem('criket_menu_categories', JSON.stringify(orderedCategories));
    // Save to Supabase so employee pages can read the order
    if (isSupabaseConfigured()) {
      try {
        const result = await supabaseClient.saveSettings('menu_category_order', orderedCategories);
        if (!result) {
          console.warn('[MenuView] saveSettings returned null — settings table may not exist');
          showToast('warning', 'Orden no guardado en BD', 'La tabla "settings" puede no existir en Supabase. Ejecuta el SQL de configuración.');
        }
      } catch (e: any) {
        console.error('Error saving category order to Supabase:', e);
        showToast('error', 'Error guardando orden', `No se pudo guardar el orden en Supabase: ${e.message || 'Error desconocido'}. Verifica que la tabla "settings" exista con columna "key" UNIQUE.`);
      }
    }
  }, [showToast]);
  useEffect(() => {
    loadData();
  }, []);
  // Real-time subscription to menu_items table
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    const client = getSupabase();
    if (!client) return;
    const subscription = client.channel('menu-items-realtime').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'menu_items'
    }, () => {
      // Don't refresh while modal is open - it resets the form
      if (!isModalOpen) {
        loadDataSilent();
      }
    }).on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'menu_categories'
    }, () => {
      if (!isModalOpen) {
        loadDataSilent();
      }
    }).subscribe();
    return () => {
      subscription?.unsubscribe();
    };
  }, [isModalOpen]);
  // Polling backup every 10s - but PAUSE while modal is open
  useEffect(() => {
    const interval = setInterval(() => {
      // Don't refresh while editing - it causes the modal to reset
      if (!isModalOpen) {
        loadDataSilent();
      }
    }, 10000);
    return () => clearInterval(interval);
  }, [isModalOpen]);
  const loadDataSilent = async () => {
    try {
      if (isSupabaseConfigured()) {
        const [items, cats] = await Promise.all([supabaseClient.getMenuItems(), supabaseClient.getMenuCategories()]);
        setMenuItems(items || []);
        if (cats.length > 0) {
          // Read saved order from Supabase settings (source of truth)
          let savedOrderArray: string[] = [];
          try {
            const supabaseOrder = await supabaseClient.getSettings('menu_category_order');
            if (supabaseOrder && Array.isArray(supabaseOrder) && supabaseOrder.length > 0) {
              savedOrderArray = supabaseOrder;
            }
          } catch (e) {
            // Fallback to localStorage if Supabase settings fails
            const localOrder = localStorage.getItem('criket_menu_categories');
            savedOrderArray = localOrder ? JSON.parse(localOrder) : [];
          }
          if (savedOrderArray.length > 0) {
            const orderedCats = savedOrderArray.filter(c => cats.includes(c));
            const newCats = cats.filter(c => !savedOrderArray.includes(c));
            setCategories([...orderedCats, ...newCats]);
          } else {
            setCategories(cats);
          }
        }
      }
    } catch (error) {
      console.error('Error auto-refreshing menu:', error);
    }
  };
  const loadData = async () => {
    setLoading(true);
    try {
      if (isSupabaseConfigured()) {
        // Fetch menu items, categories, AND saved order in parallel
        const [items, cats, supabaseOrder] = await Promise.all([supabaseClient.getMenuItems(), supabaseClient.getMenuCategories(), supabaseClient.getSettings('menu_category_order').catch(() => null)]);
        setMenuItems(items || []);
        const supabaseCats = cats.length > 0 ? cats : [];
        // Determine the best order source: Supabase settings > localStorage > alphabetical
        let savedOrderArray: string[] = [];
        if (supabaseOrder && Array.isArray(supabaseOrder) && supabaseOrder.length > 0) {
          savedOrderArray = supabaseOrder;
        } else {
          // Fallback to localStorage only if Supabase has no saved order
          const localOrder = localStorage.getItem('criket_menu_categories');
          if (localOrder) {
            try {
              const parsed = JSON.parse(localOrder);
              if (Array.isArray(parsed) && parsed.length > 0) {
                savedOrderArray = parsed;
              }
            } catch {}
          }
        }
        if (savedOrderArray.length > 0 && supabaseCats.length > 0) {
          const orderedCats = savedOrderArray.filter(c => supabaseCats.includes(c));
          const newCats = supabaseCats.filter(c => !savedOrderArray.includes(c));
          const finalCats = [...orderedCats, ...newCats];
          setCategories(finalCats);
          // Sync localStorage with the resolved order (for offline use)
          localStorage.setItem('criket_menu_categories', JSON.stringify(finalCats));
          // Only save back to Supabase if there are new categories not in the saved order
          if (newCats.length > 0) {
            saveCategoryOrder(finalCats);
          }
        } else {
          setCategories(supabaseCats);
          // Only save if there was NO saved order at all (first time setup)
          if (savedOrderArray.length === 0 && supabaseCats.length > 0) {
            saveCategoryOrder(supabaseCats);
          }
          localStorage.setItem('criket_menu_categories', JSON.stringify(supabaseCats));
        }
      } else {
        const savedItems = localStorage.getItem('criket_menu_items');
        const savedCats = localStorage.getItem('criket_menu_categories');
        setMenuItems(savedItems ? JSON.parse(savedItems) : []);
        setCategories(savedCats ? JSON.parse(savedCats) : []);
      }
    } catch (error) {
      console.error('Error loading menu data:', error);
      const savedItems = localStorage.getItem('criket_menu_items');
      const savedCats = localStorage.getItem('criket_menu_categories');
      setMenuItems(savedItems ? JSON.parse(savedItems) : []);
      setCategories(savedCats ? JSON.parse(savedCats) : []);
    } finally {
      setLoading(false);
    }
  };
  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const data = await supabaseClient.getSectionTemplates();
      setTemplates(data);
    } catch (error) {
      console.error('Error loading templates:', error);
      showToast('error', 'Error', 'No se pudieron cargar las plantillas');
    } finally {
      setLoadingTemplates(false);
    }
  };
  const handleOpenTemplates = () => {
    loadTemplates();
    setShowTemplatesModal(true);
  };
  const handleCreateTemplate = async () => {
    if (!newTemplateName.trim()) {
      showToast('warning', 'Campo requerido', 'Ingresa un nombre para la plantilla');
      return;
    }
    if (templates.some(t => t.name.toLowerCase() === newTemplateName.trim().toLowerCase())) {
      showToast('warning', 'Nombre duplicado', 'Ya existe una plantilla con este nombre');
      return;
    }
    try {
      const newTemplate = await supabaseClient.createSectionTemplate({
        name: newTemplateName.trim(),
        description: '',
        required: false,
        minSelections: 0,
        maxSelections: 1
      });
      setTemplates([...templates, {
        ...newTemplate,
        options: []
      }]);
      setNewTemplateName('');
      setExpandedTemplateId(newTemplate.id);
      setEditingTemplateId(newTemplate.id);
      showToast('success', 'Plantilla creada', 'Ahora puedes agregarle opciones');
      addLog('Plantilla Creada', newTemplate.name, 'menu');
    } catch (error) {
      console.error('Error creating template:', error);
      showToast('error', 'Error', 'No se pudo crear la plantilla');
    }
  };
  const handleUpdateTemplate = async (id: string, updates: any) => {
    try {
      await supabaseClient.updateSectionTemplate(id, updates);
      setTemplates(templates.map(t => t.id === id ? {
        ...t,
        ...updates
      } : t));
      setEditingTemplateId(null);
      showToast('success', 'Guardado', 'Plantilla actualizada');
    } catch (error) {
      console.error('Error updating template:', error);
      showToast('error', 'Error', 'No se pudo actualizar');
    }
  };
  const handleDeleteTemplate = async (id: string, name: string) => {
    if (!window.confirm(`¿Estás seguro de eliminar la plantilla "${name}"?`)) return;
    try {
      await supabaseClient.deleteSectionTemplate(id);
      setTemplates(templates.filter(t => t.id !== id));
      if (expandedTemplateId === id) setExpandedTemplateId(null);
      if (editingTemplateId === id) setEditingTemplateId(null);
      showToast('success', 'Eliminada', 'Plantilla eliminada correctamente');
      addLog('Plantilla Eliminada', name, 'menu');
    } catch (error) {
      console.error('Error deleting template:', error);
      showToast('error', 'Error', 'No se pudo eliminar');
    }
  };
  const handleAddTemplateOption = async (templateId: string) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) return;
    try {
      const newOption = await supabaseClient.createTemplateOption({
        templateId,
        name: 'Nueva opción',
        priceModifier: 0,
        available: true,
        displayOrder: template.options?.length || 0
      });
      setTemplates(templates.map(t => {
        if (t.id === templateId) {
          return {
            ...t,
            options: [...(t.options || []), newOption]
          };
        }
        return t;
      }));
    } catch (error) {
      console.error('Error adding option:', error);
      showToast('error', 'Error', 'No se pudo agregar la opción');
    }
  };
  const handleUpdateTemplateOption = async (templateId: string, optionId: string, updates: any) => {
    try {
      await supabaseClient.updateTemplateOption(optionId, updates);
      setTemplates(templates.map(t => {
        if (t.id === templateId) {
          return {
            ...t,
            options: t.options.map((o: any) => o.id === optionId ? {
              ...o,
              ...updates
            } : o)
          };
        }
        return t;
      }));
    } catch (error) {
      console.error('Error updating option:', error);
      showToast('error', 'Error', 'No se pudo actualizar la opción');
    }
  };
  const handleDeleteTemplateOption = async (templateId: string, optionId: string) => {
    try {
      await supabaseClient.deleteTemplateOption(optionId);
      setTemplates(templates.map(t => {
        if (t.id === templateId) {
          return {
            ...t,
            options: t.options.filter((o: any) => o.id !== optionId)
          };
        }
        return t;
      }));
    } catch (error) {
      console.error('Error deleting option:', error);
      showToast('error', 'Error', 'No se pudo eliminar la opción');
    }
  };
  const handleOpenApplyTemplate = (template: any) => {
    setApplyingTemplate(template);
    setSelectedItemIds([]);
    setSearchItemTerm('');
  };
  const handleApplyTemplate = async () => {
    if (!applyingTemplate || selectedItemIds.length === 0) return;
    setIsApplying(true);
    try {
      const result = await supabaseClient.applyTemplateToItems(applyingTemplate.id, selectedItemIds);
      if (result?.success) {
        showToast('success', 'Plantilla Aplicada', `Se aplicó a ${result.applied_count} platos correctamente`);
        addLog('Plantilla Aplicada', `${applyingTemplate.name} a ${result.applied_count} platos`, 'menu');
        setApplyingTemplate(null);
      } else {
        throw new Error('Error al aplicar');
      }
    } catch (error) {
      console.error('Error applying template:', error);
      showToast('error', 'Error', 'Hubo un problema al aplicar la plantilla');
    } finally {
      setIsApplying(false);
    }
  };
  const toggleItemSelection = (id: string) => {
    setSelectedItemIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };
  const toggleCategorySelection = (category: string, isSelected: boolean) => {
    const itemsInCategory = menuItems.filter(i => i.category === category).map(i => i.id);
    if (isSelected) {
      // Add all items in category that aren't already selected
      setSelectedItemIds(prev => {
        const newIds = itemsInCategory.filter(id => !prev.includes(id));
        return [...prev, ...newIds];
      });
    } else {
      // Remove all items in category
      setSelectedItemIds(prev => prev.filter(id => !itemsInCategory.includes(id)));
    }
  };
  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      showToast('warning', 'Campo requerido', 'Por favor ingresa un nombre para la categoría');
      return;
    }
    if (categories.includes(newCategoryName.trim())) {
      showToast('warning', 'Categoría existente', 'Esta categoría ya existe');
      return;
    }
    const categoryName = newCategoryName.trim();
    try {
      if (isSupabaseConfigured()) {
        await supabaseClient.createMenuCategory(categoryName);
      }
      const newCategories = [...categories, categoryName];
      setCategories(newCategories);
      saveCategoryOrder(newCategories);
      addLog('Categoría Creada', categoryName, 'menu');
      showToast('success', 'Categoría creada', `"${categoryName}" agregada exitosamente`);
      setNewCategoryName('');
    } catch (error) {
      console.error('Error creating category:', error);
      showToast('error', 'Error', 'No se pudo crear la categoría');
    }
  };
  const handleDeleteCategory = (category: string) => {
    const itemsInCategory = menuItems.filter(item => item.category === category);
    if (itemsInCategory.length > 0) {
      showToast('warning', 'Categoría en uso', `Hay ${itemsInCategory.length} plato(s) en esta categoría. Elimínalos primero.`);
      return;
    }
    setCategoryToDelete(category);
  };
  const confirmDeleteCategory = async () => {
    if (categoryToDelete) {
      try {
        if (isSupabaseConfigured()) {
          await supabaseClient.deleteMenuCategory(categoryToDelete);
        }
        const newCategories = categories.filter(c => c !== categoryToDelete);
        setCategories(newCategories);
        saveCategoryOrder(newCategories);
        addLog('Categoría Eliminada', categoryToDelete, 'menu');
        showToast('success', 'Categoría eliminada', `"${categoryToDelete}" ha sido eliminada`);
        setCategoryToDelete(null);
        if (selectedCategory === categoryToDelete) {
          setSelectedCategory('Todos');
        }
      } catch (error) {
        console.error('Error deleting category:', error);
        showToast('error', 'Error', 'No se pudo eliminar la categoría');
      }
    }
  };
  const handleMoveCategory = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= categories.length) return;
    const newCategories = [...categories];
    const temp = newCategories[index];
    newCategories[index] = newCategories[newIndex];
    newCategories[newIndex] = temp;
    setCategories(newCategories);
    // Save order — now async with error feedback
    saveCategoryOrder(newCategories).then(() => {
      console.log('[MenuView] Category order saved successfully');
    });
    if (editingCategoryIndex === index) setEditingCategoryIndex(newIndex);else if (editingCategoryIndex === newIndex) setEditingCategoryIndex(index);
  };
  const handleStartRenameCategory = (index: number) => {
    setEditingCategoryIndex(index);
    setEditingCategoryName(categories[index]);
  };
  const handleRenameCategory = async (index: number) => {
    const newName = editingCategoryName.trim();
    const oldName = categories[index];
    if (!newName) {
      showToast('warning', 'Campo requerido', 'El nombre no puede estar vacío');
      return;
    }
    if (newName === oldName) {
      setEditingCategoryIndex(null);
      return;
    }
    if (categories.includes(newName)) {
      showToast('warning', 'Nombre duplicado', 'Ya existe una categoría con ese nombre');
      return;
    }
    try {
      const newCategories = [...categories];
      newCategories[index] = newName;
      setCategories(newCategories);
      saveCategoryOrder(newCategories);
      localStorage.setItem('criket_menu_categories', JSON.stringify(newCategories));
      const updatedItems = menuItems.map(item => item.category === oldName ? {
        ...item,
        category: newName
      } : item);
      setMenuItems(updatedItems);
      localStorage.setItem('criket_menu_items', JSON.stringify(updatedItems));
      if (isSupabaseConfigured()) {
        try {
          await supabaseClient.deleteMenuCategory(oldName);
          await supabaseClient.createMenuCategory(newName);
        } catch (e) {
          console.error('Error updating category in Supabase:', e);
        }
        for (const item of menuItems.filter(i => i.category === oldName)) {
          try {
            await supabaseClient.updateMenuItem(item.id, {
              category: newName
            });
          } catch (e) {
            console.error('Error updating item category:', e);
          }
        }
      }
      addLog('Categoría Renombrada', `"${oldName}" → "${newName}"`, 'menu');
      showToast('success', 'Categoría renombrada', `"${oldName}" ahora es "${newName}"`);
      if (selectedCategory === oldName) {
        setSelectedCategory(newName);
      }
      setEditingCategoryIndex(null);
    } catch (error) {
      console.error('Error renaming category:', error);
      showToast('error', 'Error', 'No se pudo renombrar la categoría');
    }
  };
  const handleAddMenuItem = async (newItem: any) => {
    try {
      if (editingItem) {
        const updatedItem = {
          ...newItem,
          price: parseFloat(newItem.price)
        };
        if (isSupabaseConfigured()) {
          await supabaseClient.updateMenuItem(editingItem.id, updatedItem);
        }
        const updatedItems = menuItems.map(item => item.id === editingItem.id ? {
          ...item,
          ...updatedItem
        } : item);
        setMenuItems(updatedItems);
        localStorage.setItem('criket_menu_items', JSON.stringify(updatedItems));
        addLog('Plato Actualizado', `${newItem.name} - ${newItem.category}`, 'menu');
        showToast('success', 'Plato actualizado', `"${newItem.name}" ha sido actualizado`);
        setEditingItem(null);
      } else {
        const item = {
          ...newItem,
          id: `menu-${Date.now()}`,
          price: parseFloat(newItem.price)
        };
        if (isSupabaseConfigured()) {
          await supabaseClient.createMenuItem(item);
        }
        const updatedItems = [...menuItems, item];
        setMenuItems(updatedItems);
        localStorage.setItem('criket_menu_items', JSON.stringify(updatedItems));
        addLog('Plato Creado', `${newItem.name} - ${newItem.category}`, 'menu');
        showToast('success', 'Plato creado', `"${newItem.name}" ha sido agregado al menú`);
      }
    } catch (error) {
      console.error('Error saving menu item:', error);
      showToast('error', 'Error', 'No se pudo guardar el plato');
    }
  };
  const handleEdit = (item: any) => {
    setEditingItem(item);
    setIsModalOpen(true);
  };
  const handleDelete = (item: any) => {
    setDeleteConfirm(item);
  };
  const toggleAvailability = async (item: any) => {
    try {
      const newAvailability = !item.available;
      if (isSupabaseConfigured()) {
        await supabaseClient.updateMenuItem(item.id, {
          available: newAvailability
        });
      }
      const updatedItems = menuItems.map(i => i.id === item.id ? {
        ...i,
        available: newAvailability
      } : i);
      setMenuItems(updatedItems);
      localStorage.setItem('criket_menu_items', JSON.stringify(updatedItems));
      addLog('Disponibilidad Actualizada', `${item.name} ahora está ${newAvailability ? 'disponible' : 'no disponible'}`, 'menu');
      showToast('success', 'Estado actualizado', `${item.name} ahora está ${newAvailability ? 'disponible' : 'no disponible'}`);
    } catch (error) {
      console.error('Error toggling availability:', error);
      showToast('error', 'Error', 'No se pudo actualizar el estado');
    }
  };
  const confirmDelete = async () => {
    if (deleteConfirm) {
      try {
        if (isSupabaseConfigured()) {
          await supabaseClient.deleteMenuItem(deleteConfirm.id);
        }
        const updatedItems = menuItems.filter(item => item.id !== deleteConfirm.id);
        setMenuItems(updatedItems);
        localStorage.setItem('criket_menu_items', JSON.stringify(updatedItems));
        addLog('Plato Eliminado', `${deleteConfirm.name} - ${deleteConfirm.category}`, 'menu');
        showToast('success', 'Plato eliminado', `"${deleteConfirm.name}" ha sido eliminado`);
        setDeleteConfirm(null);
      } catch (error) {
        console.error('Error deleting menu item:', error);
        showToast('error', 'Error', 'No se pudo eliminar el plato');
      }
    }
  };
  const handleDeleteAll = async () => {
    if (deleteAllConfirmText !== 'ELIMINAR') {
      showToast('error', 'Error', 'Debes escribir ELIMINAR para confirmar');
      return;
    }
    setIsDeletingAll(true);
    try {
      if (isSupabaseConfigured()) {
        const promises = menuItems.map(item => supabaseClient.deleteMenuItem(item.id));
        await Promise.all(promises);
      }
      setMenuItems([]);
      localStorage.removeItem('criket_menu_items');
      addLog('Menú Vaciado', `Se eliminaron ${menuItems.length} platos`, 'menu');
      showToast('success', 'Menú vaciado', 'Todos los platos han sido eliminados');
      setShowDeleteAllModal(false);
      setDeleteAllConfirmText('');
    } catch (error) {
      console.error('Error deleting all items:', error);
      showToast('error', 'Error', 'No se pudieron eliminar todos los platos');
    } finally {
      setIsDeletingAll(false);
    }
  };
  const filteredItems = menuItems.filter(item => {
    const matchesCategory = selectedCategory === 'Todos' || item.category === selectedCategory;
    const matchesSearch = item.name.toLowerCase().includes(searchTerm.toLowerCase()) || item.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'price-low':
        return a.price - b.price;
      case 'price-high':
        return b.price - a.price;
      case 'category':
        return a.category.localeCompare(b.category);
      default:
        return 0;
    }
  });
  const stats = {
    total: menuItems.length,
    available: menuItems.filter(i => i.available !== false).length,
    hidden: menuItems.filter(i => i.available === false).length,
    avgPrice: menuItems.reduce((sum, i) => sum + i.price, 0) / menuItems.length || 0,
    categories: categories.length
  };
  if (loading) {
    return <div className="flex items-center justify-center min-h-[400px]" data-id="element-4309">
        <div className="text-center" data-id="element-4310">
          <Loader2 className="w-8 h-8 animate-spin text-gray-500 mx-auto mb-4" data-id="element-4311" />
          <p className="text-gray-500 dark:text-gray-400" data-id="element-4312">Cargando menú...</p>
        </div>
      </div>;
  }
  return <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto space-y-6" data-id="element-4313">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-4314">
        <div data-id="element-4315">
          <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white" data-id="element-4316">
            Menú
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1" data-id="element-4317">
            Administra platos, categorías y disponibilidad
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2" data-id="element-4318">
          {canDelete && menuItems.length > 0 && <Button variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20" leftIcon={<Trash2 size={16} data-id="element-4320" />} onClick={() => setShowDeleteAllModal(true)} data-id="element-4319">
              Eliminar Todos
            </Button>}
          {canManageCategories && <>
              <Button variant="outline" leftIcon={<Layers size={16} data-id="element-4322" />} onClick={handleOpenTemplates} data-id="element-4321">
                Plantillas
              </Button>
              <Button variant="outline" leftIcon={<Tag size={16} data-id="element-4324" />} onClick={() => setShowCategoryModal(true)} data-id="element-4323">
                Categorías
              </Button>
            </>}
          {canCreate && <Button leftIcon={<Plus size={16} data-id="element-4326" />} onClick={() => {
          setEditingItem(null);
          setIsModalOpen(true);
        }} className="shadow-md hover:shadow-lg transition-shadow" data-id="element-4325">
              Nuevo Plato
            </Button>}
        </div>
      </div>

      {/* Stats Cards - 5 columns */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3" data-id="element-4327">
        <div className="bg-white dark:bg-gray-900 rounded-xl border-t-4 border-t-blue-500 border border-gray-200 dark:border-gray-800 p-3 sm:p-4" data-id="element-4328">
          <div className="flex items-center gap-3" data-id="element-4329">
            <div className="w-9 h-9 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center shrink-0" data-id="element-4330">
              <Utensils size={18} className="text-blue-600 dark:text-blue-400" data-id="element-4331" />
            </div>
            <div data-id="element-4332">
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4333">Total</p>
              <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-4334">
                {stats.total}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border-t-4 border-t-emerald-500 border border-gray-200 dark:border-gray-800 p-3 sm:p-4" data-id="element-4335">
          <div className="flex items-center gap-3" data-id="element-4336">
            <div className="w-9 h-9 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center shrink-0" data-id="element-4337">
              <CheckCircle size={18} className="text-emerald-600 dark:text-emerald-400" data-id="element-4338" />
            </div>
            <div data-id="element-4339">
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4340">
                Disponibles
              </p>
              <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-4341">
                {stats.available}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border-t-4 border-t-amber-500 border border-gray-200 dark:border-gray-800 p-3 sm:p-4" data-id="element-4342">
          <div className="flex items-center gap-3" data-id="element-4343">
            <div className="w-9 h-9 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center shrink-0" data-id="element-4344">
              <EyeOff size={18} className="text-amber-600 dark:text-amber-400" data-id="element-4345" />
            </div>
            <div data-id="element-4346">
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4347">
                Ocultos
              </p>
              <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-4348">
                {stats.hidden}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border-t-4 border-t-indigo-500 border border-gray-200 dark:border-gray-800 p-3 sm:p-4" data-id="element-4349">
          <div className="flex items-center gap-3" data-id="element-4350">
            <div className="w-9 h-9 rounded-full bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center shrink-0" data-id="element-4351">
              <DollarSign size={18} className="text-indigo-600 dark:text-indigo-400" data-id="element-4352" />
            </div>
            <div data-id="element-4353">
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4354">
                Promedio
              </p>
              <p className="text-lg font-bold text-gray-900 dark:text-white font-mono" data-id="element-4355">
                RD$ {stats.avgPrice.toFixed(0)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-gray-900 rounded-xl border-t-4 border-t-purple-500 border border-gray-200 dark:border-gray-800 p-3 sm:p-4 col-span-2 sm:col-span-1" data-id="element-4356">
          <div className="flex items-center gap-3" data-id="element-4357">
            <div className="w-9 h-9 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center shrink-0" data-id="element-4358">
              <Tag size={18} className="text-purple-600 dark:text-purple-400" data-id="element-4359" />
            </div>
            <div data-id="element-4360">
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4361">
                Categorías
              </p>
              <p className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-4362">
                {stats.categories}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Categories */}
      <div className="space-y-3" data-id="element-4363">
        {/* Search and Sort */}
        <div className="flex flex-col sm:flex-row gap-3" data-id="element-4364">
          <div className="flex-1" data-id="element-4365">
            <SearchBar placeholder="Buscar platos..." onSearch={setSearchTerm} className="w-full" data-id="element-4366" />
          </div>
          <FilterDropdown value={sortBy === 'name' ? 'Nombre A-Z' : sortBy === 'price-low' ? 'Precio ↑' : sortBy === 'price-high' ? 'Precio ↓' : 'Categoría'} options={['Nombre A-Z', 'Precio ↑', 'Precio ↓', 'Categoría']} onChange={val => {
          const map: Record<string, string> = {
            'Nombre A-Z': 'name',
            'Precio ↑': 'price-low',
            'Precio ↓': 'price-high',
            Categoría: 'category'
          };
          setSortBy(map[val] || 'name');
        }} className="w-full sm:w-40" data-id="element-4367" />
        </div>

        {/* Category Pills */}
        <div className="relative" data-id="element-4368">
          <div className="overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0 scrollbar-hide" data-id="element-4369">
            <div className="flex gap-2" data-id="element-4370">
              <button onClick={() => setSelectedCategory('Todos')} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${selectedCategory === 'Todos' ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`} data-id="element-4371">
                Todos ({menuItems.length})
              </button>
              {categories.map(category => {
              const count = menuItems.filter(i => i.category === category).length;
              return <button key={category} onClick={() => setSelectedCategory(category)} className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${selectedCategory === category ? 'bg-gray-900 dark:bg-white text-white dark:text-gray-900' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'}`} data-id="element-4372">
                    {category} ({count})
                  </button>;
            })}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {filteredItems.length === 0 ? <div className="text-center py-20 bg-white dark:bg-gray-900 rounded-xl border border-dashed border-gray-300 dark:border-gray-700" data-id="element-4373">
          <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4374">
            <ChefHat size={28} className="text-gray-400" data-id="element-4375" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white" data-id="element-4376">
            {menuItems.length === 0 ? 'Tu menú está vacío' : 'No se encontraron platos'}
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mt-1 text-sm max-w-sm mx-auto" data-id="element-4377">
            {menuItems.length === 0 ? 'Comienza agregando platos para que tus clientes puedan ordenar.' : 'Intenta ajustando los filtros o la búsqueda.'}
          </p>
          {menuItems.length === 0 && canCreate && <Button onClick={() => {
        setEditingItem(null);
        setIsModalOpen(true);
      }} leftIcon={<Plus size={16} data-id="element-4379" />} className="mt-6" data-id="element-4378">
              Agregar Primer Plato
            </Button>}
        </div> : <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 overflow-hidden" data-id="element-4380">
          {/* Desktop Table */}
          <div className="hidden lg:block" data-id="element-4381">
            <table className="w-full" data-id="element-4382">
              <thead data-id="element-4383">
                <tr className="border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30" data-id="element-4384">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-20" data-id="element-4385">
                    Foto
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4386">
                    Plato
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-32" data-id="element-4387">
                    Categoría
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28" data-id="element-4388">
                    Precio
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24" data-id="element-4389">
                    Tipo
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-28" data-id="element-4390">
                    Estado
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider w-24" data-id="element-4391">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4392">
                {filteredItems.map(item => <tr key={item.id} className={`hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors ${item.available === false ? 'opacity-60' : ''}`} data-id="element-4393">
                    <td className="px-4 py-3" data-id="element-4394">
                      <div className="w-16 h-16 rounded-xl bg-gray-100 dark:bg-gray-800 overflow-hidden border border-gray-200 dark:border-gray-700" data-id="element-4395">
                        {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-4396" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-4397">
                            <Utensils size={20} className="text-gray-400" data-id="element-4398" />
                          </div>}
                      </div>
                    </td>
                    <td className="px-4 py-3" data-id="element-4399">
                      <p className="font-semibold text-gray-900 dark:text-white" data-id="element-4400">
                        {item.name}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-xs" data-id="element-4401">
                        {item.description}
                      </p>
                    </td>
                    <td className="px-4 py-3" data-id="element-4402">
                      <span className="inline-flex px-2.5 py-1 rounded-lg text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300" data-id="element-4403">
                        {item.category}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-4404">
                      <span className="font-mono font-bold text-gray-900 dark:text-white" data-id="element-4405">
                        RD$ {item.price.toFixed(0)}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-4406">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${item.catalogType === 'Almuerzo' ? 'text-orange-600 dark:text-orange-400' : 'text-purple-600 dark:text-purple-400'}`} data-id="element-4407">
                        <span className={`w-1.5 h-1.5 rounded-full ${item.catalogType === 'Almuerzo' ? 'bg-orange-500' : 'bg-purple-500'}`} data-id="element-4408" />
                        {item.catalogType}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-4409">
                      {canEdit ? <button onClick={() => toggleAvailability(item)} className="flex items-center gap-2" data-id="element-4410">
                          <div className={`w-9 h-5 rounded-full relative transition-colors ${item.available !== false ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-4411">
                            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-all ${item.available !== false ? 'right-0.5' : 'left-0.5'}`} data-id="element-4412" />
                          </div>
                        </button> : <span className={`text-xs font-medium ${item.available !== false ? 'text-emerald-600' : 'text-gray-400'}`} data-id="element-4413">
                          {item.available !== false ? 'Visible' : 'Oculto'}
                        </span>}
                    </td>
                    <td className="px-4 py-3" data-id="element-4414">
                      <div className="flex items-center justify-end gap-1" data-id="element-4415">
                        {canEdit && <button onClick={() => handleEdit(item)} className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" data-id="element-4416">
                            <Edit2 size={16} data-id="element-4417" />
                          </button>}
                        {canDelete && <button onClick={() => handleDelete(item)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-4418">
                            <Trash2 size={16} data-id="element-4419" />
                          </button>}
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden divide-y divide-gray-100 dark:divide-gray-800" data-id="element-4420">
            {filteredItems.map(item => <div key={item.id} className={`p-4 ${item.available === false ? 'opacity-60' : ''}`} data-id="element-4421">
                <div className="flex gap-4" data-id="element-4422">
                  <div className="w-20 h-20 rounded-xl bg-gray-100 dark:bg-gray-800 overflow-hidden border border-gray-200 dark:border-gray-700 shrink-0" data-id="element-4423">
                    {item.image ? <img src={item.image} alt={item.name} className="w-full h-full object-cover" data-id="element-4424" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-4425">
                        <Utensils size={24} className="text-gray-400" data-id="element-4426" />
                      </div>}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-4427">
                    <div className="flex items-start justify-between gap-2" data-id="element-4428">
                      <p className="font-semibold text-gray-900 dark:text-white truncate" data-id="element-4429">
                        {item.name}
                      </p>
                      <span className="font-mono font-bold text-gray-900 dark:text-white whitespace-nowrap" data-id="element-4430">
                        RD$ {item.price.toFixed(0)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate mt-0.5" data-id="element-4431">
                      {item.description}
                    </p>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2" data-id="element-4432">
                      <span className="inline-flex px-2 py-0.5 rounded-md text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300" data-id="element-4433">
                        {item.category}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium ${item.catalogType === 'Almuerzo' ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'}`} data-id="element-4434">
                        {item.catalogType}
                      </span>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-medium ${item.available !== false ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-4435">
                        {item.available !== false ? 'Visible' : 'Oculto'}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100 dark:border-gray-800" data-id="element-4436">
                      {canEdit && <button onClick={() => toggleAvailability(item)} className="flex items-center gap-2" data-id="element-4437">
                          <div className={`w-8 h-4 rounded-full relative transition-colors ${item.available !== false ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-gray-600'}`} data-id="element-4438">
                            <div className={`absolute top-0.5 w-3 h-3 bg-white rounded-full shadow-sm transition-all ${item.available !== false ? 'right-0.5' : 'left-0.5'}`} data-id="element-4439" />
                          </div>
                          <span className="text-xs text-gray-500" data-id="element-4440">
                            {item.available !== false ? 'Visible' : 'Oculto'}
                          </span>
                        </button>}
                      <div className="flex items-center gap-1" data-id="element-4441">
                        {canEdit && <button onClick={() => handleEdit(item)} className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors" data-id="element-4442">
                            <Edit2 size={16} data-id="element-4443" />
                          </button>}
                        {canDelete && <button onClick={() => handleDelete(item)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-4444">
                            <Trash2 size={16} data-id="element-4445" />
                          </button>}
                      </div>
                    </div>
                  </div>
                </div>
              </div>)}
          </div>

          {/* Footer */}
          <div className="px-4 py-3 bg-gray-50/50 dark:bg-gray-800/30 border-t border-gray-200 dark:border-gray-800 flex flex-wrap items-center justify-between gap-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-4446">
            <span data-id="element-4447">
              Mostrando{' '}
              <strong className="text-gray-900 dark:text-white" data-id="element-4448">
                {filteredItems.length}
              </strong>{' '}
              de {menuItems.length} platos
            </span>
            <span data-id="element-4449">
              Precio promedio:{' '}
              <strong className="text-gray-900 dark:text-white font-mono" data-id="element-4450">
                RD$ {stats.avgPrice.toFixed(0)}
              </strong>
            </span>
          </div>
        </div>}

      {/* Modals */}
      <MenuItemFormModal isOpen={isModalOpen} onClose={() => {
      setIsModalOpen(false);
      setEditingItem(null);
    }} onSave={handleAddMenuItem} editingItem={editingItem} availableCategories={categories} data-id="element-4451" />

      {/* Category Management Modal */}
      {showCategoryModal && <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" data-id="element-4452">
          <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-lg border border-gray-200 dark:border-gray-800 overflow-hidden max-h-[95vh] sm:max-h-[85vh] flex flex-col" data-id="element-4453">
            <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center" data-id="element-4454">
              <div data-id="element-4455">
                <h2 className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-4456">
                  Gestionar Categorías
                </h2>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-4457">
                  Ordena, renombra o elimina categorías del menú
                </p>
              </div>
              <button onClick={() => {
            setShowCategoryModal(false);
            setEditingCategoryIndex(null);
          }} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors" data-id="element-4458">
                <X size={20} data-id="element-4459" />
              </button>
            </div>
            <div className="px-6 py-4 bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800" data-id="element-4460">
              <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2" data-id="element-4461">
                Nueva Categoría
              </label>
              <div className="flex gap-2" data-id="element-4462">
                <input type="text" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleAddCategory()} placeholder="Ej: Postres, Bebidas..." className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-4463" />
                <Button onClick={handleAddCategory} leftIcon={<Plus size={16} data-id="element-4465" />} size="md" data-id="element-4464">
                  Agregar
                </Button>
              </div>
            </div>
            <div className="px-6 py-4" data-id="element-4466">
              <div className="flex items-center justify-between mb-3" data-id="element-4467">
                <p className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider" data-id="element-4468">
                  Categorías ({categories.length})
                </p>
                <p className="text-[10px] text-gray-400 dark:text-gray-500" data-id="element-4469">
                  Usa las flechas para reordenar
                </p>
              </div>
              {categories.length === 0 ? <div className="text-center py-10 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-4470">
                  <Tag size={24} className="text-gray-300 dark:text-gray-600 mx-auto mb-2" data-id="element-4471" />
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4472">
                    No hay categorías creadas
                  </p>
                </div> : <div className="space-y-1.5 max-h-[350px] overflow-y-auto pr-1" data-id="element-4473">
                  {categories.map((category, index) => {
              const itemCount = menuItems.filter(item => item.category === category).length;
              const isEditing = editingCategoryIndex === index;
              return <div key={`${category}-${index}`} className={`flex items-center gap-2 p-3 rounded-xl border transition-all ${isEditing ? 'bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800' : 'bg-white dark:bg-gray-800/50 border-gray-100 dark:border-gray-800 hover:border-gray-200 dark:hover:border-gray-700'}`} data-id="element-4474">
                        <span className="w-6 h-6 rounded-md bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-[10px] font-bold text-gray-500 dark:text-gray-400 shrink-0" data-id="element-4475">
                          {index + 1}
                        </span>
                        <div className="flex flex-col shrink-0" data-id="element-4476">
                          <button onClick={() => handleMoveCategory(index, 'up')} disabled={index === 0} className={`p-0.5 rounded transition-colors ${index === 0 ? 'text-gray-200 dark:text-gray-700 cursor-not-allowed' : 'text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`} title="Mover arriba" data-id="element-4477">
                            <ArrowUp size={13} data-id="element-4478" />
                          </button>
                          <button onClick={() => handleMoveCategory(index, 'down')} disabled={index === categories.length - 1} className={`p-0.5 rounded transition-colors ${index === categories.length - 1 ? 'text-gray-200 dark:text-gray-700 cursor-not-allowed' : 'text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700'}`} title="Mover abajo" data-id="element-4479">
                            <ArrowDown size={13} data-id="element-4480" />
                          </button>
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-4481">
                          {isEditing ? <input type="text" value={editingCategoryName} onChange={e => setEditingCategoryName(e.target.value)} onKeyPress={e => {
                    if (e.key === 'Enter') handleRenameCategory(index);
                    if (e.key === 'Escape') setEditingCategoryIndex(null);
                  }} autoFocus className="w-full px-3 py-1.5 border border-blue-300 dark:border-blue-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-4482" /> : <div data-id="element-4483">
                              <p className="font-semibold text-gray-900 dark:text-white text-sm truncate" data-id="element-4484">
                                {category}
                              </p>
                              <p className="text-[11px] text-gray-500 dark:text-gray-400" data-id="element-4485">
                                {itemCount} plato{itemCount !== 1 ? 's' : ''}
                              </p>
                            </div>}
                        </div>
                        <div className="flex items-center gap-1 shrink-0" data-id="element-4486">
                          {isEditing ? <>
                              <button onClick={() => handleRenameCategory(index)} className="p-1.5 text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors" title="Guardar nombre" data-id="element-4487">
                                <Check size={15} data-id="element-4488" />
                              </button>
                              <button onClick={() => setEditingCategoryIndex(null)} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" title="Cancelar" data-id="element-4489">
                                <X size={15} data-id="element-4490" />
                              </button>
                            </> : <>
                              <button onClick={() => handleStartRenameCategory(index)} className="p-1.5 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" title="Renombrar" data-id="element-4491">
                                <Edit2 size={14} data-id="element-4492" />
                              </button>
                              <button onClick={() => handleDeleteCategory(category)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Eliminar" data-id="element-4493">
                                <Trash2 size={14} data-id="element-4494" />
                              </button>
                            </>}
                        </div>
                      </div>;
            })}
                </div>}
            </div>
            <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/30" data-id="element-4495">
              <Button variant="outline" onClick={() => {
            setShowCategoryModal(false);
            setEditingCategoryIndex(null);
          }} fullWidth data-id="element-4496">
                Cerrar
              </Button>
            </div>
          </div>
        </div>}

      {/* Templates Management Modal */}
      {showTemplatesModal && <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/60 p-0 sm:p-4" data-id="element-4497">
          <div className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl border border-gray-200 dark:border-gray-800 overflow-hidden h-[95vh] sm:h-[85vh] flex flex-col" data-id="element-4498">
            {applyingTemplate /* SUB-VIEW: Apply Template to Items */ ? <div className="flex flex-col h-full" data-id="element-4499">
                <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-900 shrink-0" data-id="element-4500">
                  <div className="flex items-center gap-3" data-id="element-4501">
                    <button onClick={() => setApplyingTemplate(null)} className="p-2 -ml-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors" data-id="element-4502">
                      <ArrowLeft size={20} data-id="element-4503" />
                    </button>
                    <div data-id="element-4504">
                      <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-4505">
                        Aplicar Plantilla
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 font-medium" data-id="element-4506">
                        {applyingTemplate.name}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => {
              setApplyingTemplate(null);
              setShowTemplatesModal(false);
            }} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors" data-id="element-4507">
                    <X size={20} data-id="element-4508" />
                  </button>
                </div>

                <div className="p-4 bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800 shrink-0" data-id="element-4509">
                  <div className="relative" data-id="element-4510">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} data-id="element-4511" />
                    <input type="text" placeholder="Buscar platos..." value={searchItemTerm} onChange={e => setSearchItemTerm(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white text-sm" data-id="element-4512" />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6" data-id="element-4513">
                  {categories.map(category => {
              const categoryItems = menuItems.filter(i => i.category === category && i.name.toLowerCase().includes(searchItemTerm.toLowerCase()));
              if (categoryItems.length === 0) return null;
              const allSelected = categoryItems.every(i => selectedItemIds.includes(i.id));
              const someSelected = categoryItems.some(i => selectedItemIds.includes(i.id));
              return <div key={category} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-4514">
                        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center" data-id="element-4515">
                          <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-4516">
                            <Tag size={16} className="text-gray-400" data-id="element-4517" />
                            {category}
                            <span className="text-xs font-normal text-gray-500 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded-full" data-id="element-4518">
                              {categoryItems.length}
                            </span>
                          </h3>
                          <button onClick={() => toggleCategorySelection(category, !allSelected)} className="text-xs font-bold text-blue-600 dark:text-blue-400 hover:underline" data-id="element-4519">
                            {allSelected ? 'Deseleccionar Todos' : 'Seleccionar Todos'}
                          </button>
                        </div>
                        <div className="divide-y divide-gray-100 dark:divide-gray-700/50" data-id="element-4520">
                          {categoryItems.map(item => <label key={item.id} className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-700/30 cursor-pointer transition-colors" data-id="element-4521">
                              <input type="checkbox" checked={selectedItemIds.includes(item.id)} onChange={() => toggleItemSelection(item.id)} className="w-5 h-5 rounded border-gray-300 text-gray-900 focus:ring-gray-900 cursor-pointer" data-id="element-4522" />
                              <div className="w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-700 overflow-hidden shrink-0" data-id="element-4523">
                                {item.image ? <img src={item.image} alt="" className="w-full h-full object-cover" data-id="element-4524" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-4525">
                                    <Utensils size={16} className="text-gray-400" data-id="element-4526" />
                                  </div>}
                              </div>
                              <div className="flex-1 min-w-0" data-id="element-4527">
                                <p className="font-medium text-sm text-gray-900 dark:text-white truncate" data-id="element-4528">
                                  {item.name}
                                </p>
                                <p className="text-xs text-gray-500 font-mono" data-id="element-4529">
                                  RD$ {item.price.toFixed(2)}
                                </p>
                              </div>
                            </label>)}
                        </div>
                      </div>;
            })}

                  {menuItems.filter(i => i.name.toLowerCase().includes(searchItemTerm.toLowerCase())).length === 0 && <div className="text-center py-10" data-id="element-4530">
                      <Search size={32} className="text-gray-300 mx-auto mb-3" data-id="element-4531" />
                      <p className="text-gray-500" data-id="element-4532">No se encontraron platos</p>
                    </div>}
                </div>

                <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 shrink-0 flex items-center justify-between" data-id="element-4533">
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300" data-id="element-4534">
                    <strong className="text-gray-900 dark:text-white" data-id="element-4535">
                      {selectedItemIds.length}
                    </strong>{' '}
                    platos seleccionados
                  </p>
                  <div className="flex gap-3" data-id="element-4536">
                    <Button variant="outline" onClick={() => setApplyingTemplate(null)} disabled={isApplying} data-id="element-4537">
                      Cancelar
                    </Button>
                    <Button onClick={handleApplyTemplate} disabled={selectedItemIds.length === 0 || isApplying} leftIcon={isApplying ? <Loader2 size={16} className="animate-spin" data-id="element-4539" /> : <Check size={16} data-id="element-4540" />} data-id="element-4538">
                      {isApplying ? 'Aplicando...' : 'Aplicar Plantilla'}
                    </Button>
                  </div>
                </div>
              </div> /* MAIN VIEW: Manage Templates */ : <div className="flex flex-col h-full" data-id="element-4541">
                <div className="px-6 py-5 border-b border-gray-100 dark:border-gray-800 flex justify-between items-center shrink-0" data-id="element-4542">
                  <div data-id="element-4543">
                    <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-4544">
                      <Layers size={20} className="text-blue-500" data-id="element-4545" />
                      Plantillas de Opciones
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5" data-id="element-4546">
                      Crea modificadores reutilizables y aplícalos a múltiples
                      platos
                    </p>
                  </div>
                  <button onClick={() => setShowTemplatesModal(false)} className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-xl transition-colors" data-id="element-4547">
                    <X size={20} data-id="element-4548" />
                  </button>
                </div>

                <div className="px-6 py-4 bg-gray-50/50 dark:bg-gray-800/30 border-b border-gray-100 dark:border-gray-800 shrink-0" data-id="element-4549">
                  <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2" data-id="element-4550">
                    Nueva Plantilla
                  </label>
                  <div className="flex gap-2" data-id="element-4551">
                    <input type="text" value={newTemplateName} onChange={e => setNewTemplateName(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleCreateTemplate()} placeholder="Ej: Tipo de Pan, Salsas Extra, Término de Carne..." className="flex-1 px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-white bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm" data-id="element-4552" />
                    <Button onClick={handleCreateTemplate} leftIcon={<Plus size={16} data-id="element-4554" />} size="md" data-id="element-4553">
                      Crear
                    </Button>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4 sm:p-6 bg-gray-50/30 dark:bg-gray-900/50" data-id="element-4555">
                  {loadingTemplates ? <div className="flex justify-center py-20" data-id="element-4556">
                      <Loader2 className="w-8 h-8 animate-spin text-gray-400" data-id="element-4557" />
                    </div> : templates.length === 0 ? <div className="text-center py-16 bg-white dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-700" data-id="element-4558">
                      <Layers size={40} className="text-gray-300 dark:text-gray-600 mx-auto mb-4" data-id="element-4559" />
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1" data-id="element-4560">
                        No hay plantillas
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto" data-id="element-4561">
                        Crea tu primera plantilla arriba para empezar a
                        reutilizar opciones en tus platos.
                      </p>
                    </div> : <div className="space-y-4" data-id="element-4562">
                      {templates.map(template => {
                const isExpanded = expandedTemplateId === template.id;
                const isEditing = editingTemplateId === template.id;
                return <div key={template.id} className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden transition-all" data-id="element-4563">
                            {/* Template Header */}
                            <div className="p-4 sm:p-5 flex items-start gap-4" data-id="element-4564">
                              <button onClick={() => setExpandedTemplateId(isExpanded ? null : template.id)} className="mt-1 p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors" data-id="element-4565">
                                {isExpanded ? <ChevronUp size={20} data-id="element-4566" /> : <ChevronDown size={20} data-id="element-4567" />}
                              </button>

                              <div className="flex-1 min-w-0" data-id="element-4568">
                                {isEditing ? <div className="space-y-4" data-id="element-4569">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-4570">
                                      <input type="text" value={template.name} onChange={e => setTemplates(templates.map(t => t.id === template.id ? {
                            ...t,
                            name: e.target.value
                          } : t))} placeholder="Nombre de la plantilla" className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-sm" autoFocus data-id="element-4571" />
                                      <input type="text" value={template.description || ''} onChange={e => setTemplates(templates.map(t => t.id === template.id ? {
                            ...t,
                            description: e.target.value
                          } : t))} placeholder="Descripción (opcional)" className="w-full px-3 py-2 border border-gray-200 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-sm" data-id="element-4572" />
                                    </div>

                                    <div className="flex flex-wrap items-center gap-4 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-xl border border-gray-100 dark:border-gray-700" data-id="element-4573">
                                      <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 cursor-pointer" data-id="element-4574">
                                        <input type="checkbox" checked={template.required} onChange={e => setTemplates(templates.map(t => t.id === template.id ? {
                              ...t,
                              required: e.target.checked
                            } : t))} className="w-4 h-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900" data-id="element-4575" />
                                        Obligatorio
                                      </label>
                                      <div className="h-4 w-px bg-gray-300 dark:bg-gray-600 mx-2 hidden sm:block" data-id="element-4576"></div>
                                      <div className="flex items-center gap-2" data-id="element-4577">
                                        <span className="text-xs text-gray-500 font-bold uppercase" data-id="element-4578">
                                          Mín
                                        </span>
                                        <input type="number" min="0" value={template.minSelections} onChange={e => setTemplates(templates.map(t => t.id === template.id ? {
                              ...t,
                              minSelections: parseInt(e.target.value) || 0
                            } : t))} className="w-16 px-2 py-1 text-center text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700" data-id="element-4579" />
                                      </div>
                                      <div className="flex items-center gap-2" data-id="element-4580">
                                        <span className="text-xs text-gray-500 font-bold uppercase" data-id="element-4581">
                                          Máx
                                        </span>
                                        <input type="number" min="1" value={template.maxSelections} onChange={e => setTemplates(templates.map(t => t.id === template.id ? {
                              ...t,
                              maxSelections: parseInt(e.target.value) || 1
                            } : t))} className="w-16 px-2 py-1 text-center text-sm border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700" data-id="element-4582" />
                                      </div>
                                    </div>

                                    <div className="flex gap-2 pt-2" data-id="element-4583">
                                      <Button size="sm" onClick={() => handleUpdateTemplate(template.id, template)} leftIcon={<Save size={14} data-id="element-4585" />} data-id="element-4584">
                                        Guardar
                                      </Button>
                                      <Button size="sm" variant="ghost" onClick={() => {
                            loadTemplates(); // Reload to discard changes
                            setEditingTemplateId(null);
                          }} data-id="element-4586">
                                        Cancelar
                                      </Button>
                                    </div>
                                  </div> : <div onClick={() => setExpandedTemplateId(isExpanded ? null : template.id)} className="cursor-pointer" data-id="element-4587">
                                    <div className="flex items-center gap-3 mb-1" data-id="element-4588">
                                      <h3 className="font-bold text-gray-900 dark:text-white text-base" data-id="element-4589">
                                        {template.name}
                                      </h3>
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${template.required ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300'}`} data-id="element-4590">
                                        {template.required ? 'Obligatorio' : 'Opcional'}
                                      </span>
                                    </div>
                                    {template.description && <p className="text-sm text-gray-500 dark:text-gray-400 mb-2" data-id="element-4591">
                                        {template.description}
                                      </p>}
                                    <div className="flex flex-wrap gap-2 mt-1.5" data-id="element-4592">
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300" data-id="element-4593">
                                        {template.minSelections} -{' '}
                                        {template.maxSelections} opciones
                                      </span>
                                      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300" data-id="element-4594">
                                        {template.options?.length || 0} ítems
                                      </span>
                                    </div>
                                  </div>}
                              </div>

                              <div className="flex flex-col sm:flex-row gap-1 shrink-0" data-id="element-4595">
                                {!isEditing && <>
                                    <Button size="sm" variant="outline" onClick={() => handleOpenApplyTemplate(template)} className="hidden sm:flex mr-2" data-id="element-4596">
                                      Aplicar a Platos
                                    </Button>
                                    <button onClick={() => setEditingTemplateId(template.id)} className="p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" title="Editar plantilla" data-id="element-4597">
                                      <Edit2 size={18} data-id="element-4598" />
                                    </button>
                                    <button onClick={() => handleDeleteTemplate(template.id, template.name)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" title="Eliminar plantilla" data-id="element-4599">
                                      <Trash2 size={18} data-id="element-4600" />
                                    </button>
                                  </>}
                              </div>
                            </div>

                            {/* Mobile Apply Button */}
                            {!isEditing && <div className="sm:hidden px-4 pb-4" data-id="element-4601">
                                <Button size="sm" variant="outline" fullWidth onClick={() => handleOpenApplyTemplate(template)} data-id="element-4602">
                                  Aplicar a Platos
                                </Button>
                              </div>}

                            {/* Options List */}
                            {isExpanded && <div className="border-t border-gray-100 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 p-4 sm:p-5" data-id="element-4603">
                                <div className="space-y-3 mb-4" data-id="element-4604">
                                  {template.options?.map((option: any, optIndex: number) => <div key={option.id || optIndex} className="flex flex-col sm:flex-row sm:items-center gap-3 bg-white dark:bg-gray-800 p-3 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm" data-id="element-4605">
                                        <div className="flex items-center gap-3 flex-1" data-id="element-4606">
                                          <GripVertical size={16} className="text-gray-300 dark:text-gray-600 cursor-move shrink-0" data-id="element-4607" />
                                          <input type="text" value={option.name} onChange={e => {
                            const newName = e.target.value;
                            setTemplates(templates.map(t => {
                              if (t.id === template.id) {
                                return {
                                  ...t,
                                  options: t.options.map((o: any) => o.id === option.id ? {
                                    ...o,
                                    name: newName
                                  } : o)
                                };
                              }
                              return t;
                            }));
                          }} onBlur={() => handleUpdateTemplateOption(template.id, option.id, {
                            name: option.name
                          })} placeholder="Nombre opción" className="flex-1 min-w-0 bg-transparent border-none p-0 text-sm font-medium text-gray-900 dark:text-white placeholder:text-gray-400 focus:ring-0" data-id="element-4608" />
                                        </div>

                                        <div className="flex items-center justify-between sm:justify-end gap-3 pl-7 sm:pl-0" data-id="element-4609">
                                          <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-900 rounded-lg px-2 py-1 border border-gray-100 dark:border-gray-700" data-id="element-4610">
                                            <span className="text-xs text-gray-400 font-bold" data-id="element-4611">
                                              +RD$
                                            </span>
                                            <input type="number" step="0.01" value={option.priceModifier} onChange={e => {
                              const newPrice = parseFloat(e.target.value) || 0;
                              setTemplates(templates.map(t => {
                                if (t.id === template.id) {
                                  return {
                                    ...t,
                                    options: t.options.map((o: any) => o.id === option.id ? {
                                      ...o,
                                      priceModifier: newPrice
                                    } : o)
                                  };
                                }
                                return t;
                              }));
                            }} onBlur={() => handleUpdateTemplateOption(template.id, option.id, {
                              priceModifier: option.priceModifier
                            })} className="w-16 bg-transparent border-none p-0 text-sm font-mono text-right focus:ring-0" placeholder="0" data-id="element-4612" />
                                          </div>

                                          <div className="flex items-center gap-1" data-id="element-4613">
                                            <button onClick={() => {
                              const newAvailable = !option.available;
                              setTemplates(templates.map(t => {
                                if (t.id === template.id) {
                                  return {
                                    ...t,
                                    options: t.options.map((o: any) => o.id === option.id ? {
                                      ...o,
                                      available: newAvailable
                                    } : o)
                                  };
                                }
                                return t;
                              }));
                              handleUpdateTemplateOption(template.id, option.id, {
                                available: newAvailable
                              });
                            }} className={`p-1.5 rounded-lg transition-colors ${option.available ? 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/20' : 'text-gray-400 bg-gray-100 dark:bg-gray-700'}`} title={option.available ? 'Visible' : 'Oculto'} data-id="element-4614">
                                              {option.available ? <Eye size={16} data-id="element-4615" /> : <EyeOff size={16} data-id="element-4616" />}
                                            </button>

                                            <button onClick={() => handleDeleteTemplateOption(template.id, option.id)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors" data-id="element-4617">
                                              <Trash2 size={16} data-id="element-4618" />
                                            </button>
                                          </div>
                                        </div>
                                      </div>)}

                                  {(!template.options || template.options.length === 0) && <div className="text-center py-6 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-xl" data-id="element-4619">
                                      <p className="text-sm text-gray-400" data-id="element-4620">
                                        No hay opciones en esta plantilla
                                      </p>
                                    </div>}
                                </div>

                                <Button size="sm" variant="outline" onClick={() => handleAddTemplateOption(template.id)} leftIcon={<Plus size={16} data-id="element-4622" />} fullWidth data-id="element-4621">
                                  Agregar Opción
                                </Button>
                              </div>}
                          </div>;
              })}
                    </div>}
                </div>
              </div>}
          </div>
        </div>}

      {/* Delete Category Confirmation */}
      {categoryToDelete && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-id="element-4623">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-4624">
            <div className="p-6 text-center" data-id="element-4625">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4626">
                <Trash2 className="w-8 h-8 text-red-600 dark:text-red-400" data-id="element-4627" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-4628">
                ¿Eliminar categoría?
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-4629">
                Estás a punto de eliminar la categoría{' '}
                <strong data-id="element-4630">"{categoryToDelete}"</strong>. Esta acción no se puede
                deshacer.
              </p>
              <div className="flex gap-3" data-id="element-4631">
                <Button variant="outline" onClick={() => setCategoryToDelete(null)} fullWidth data-id="element-4632">
                  Cancelar
                </Button>
                <Button onClick={confirmDeleteCategory} fullWidth className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800" data-id="element-4633">
                  Eliminar
                </Button>
              </div>
            </div>
          </div>
        </div>}

      {/* Delete Menu Item Confirmation */}
      {deleteConfirm && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-id="element-4634">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-4635">
            <div className="p-6 text-center" data-id="element-4636">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4637">
                <Trash2 className="w-8 h-8 text-red-600 dark:text-red-400" data-id="element-4638" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-4639">
                ¿Eliminar plato?
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-6" data-id="element-4640">
                Estás a punto de eliminar{' '}
                <strong data-id="element-4641">"{deleteConfirm.name}"</strong>. Esta acción no se puede
                deshacer.
              </p>
              <div className="bg-gray-50 dark:bg-gray-800 p-4 rounded-xl mb-6 text-left flex gap-4 items-center" data-id="element-4642">
                <div className="w-16 h-16 rounded-lg bg-gray-200 dark:bg-gray-700 overflow-hidden shrink-0" data-id="element-4643">
                  {deleteConfirm.image ? <img src={deleteConfirm.image} alt="" className="w-full h-full object-cover" data-id="element-4644" /> : <div className="w-full h-full flex items-center justify-center" data-id="element-4645">
                      <Utensils size={20} className="text-gray-400" data-id="element-4646" />
                    </div>}
                </div>
                <div data-id="element-4647">
                  <p className="font-bold text-gray-900 dark:text-white" data-id="element-4648">
                    {deleteConfirm.name}
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4649">
                    {deleteConfirm.category} • RD${' '}
                    {deleteConfirm.price.toFixed(2)}
                  </p>
                </div>
              </div>
              <div className="flex gap-3" data-id="element-4650">
                <Button variant="outline" onClick={() => setDeleteConfirm(null)} fullWidth data-id="element-4651">
                  Cancelar
                </Button>
                <Button onClick={confirmDelete} fullWidth className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800" data-id="element-4652">
                  Eliminar
                </Button>
              </div>
            </div>
          </div>
        </div>}

      {/* Delete All Confirmation Modal */}
      {showDeleteAllModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" data-id="element-4653">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md border border-gray-200 dark:border-gray-800" data-id="element-4654">
            <div className="p-6 text-center" data-id="element-4655">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4656">
                <AlertTriangle className="w-8 h-8 text-red-600 dark:text-red-400" data-id="element-4657" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-4658">
                ¿Eliminar TODO el menú?
              </h2>
              <p className="text-gray-600 dark:text-gray-300 mb-4" data-id="element-4659">
                Estás a punto de eliminar{' '}
                <strong data-id="element-4660">todos los platos ({menuItems.length})</strong>. Esta
                acción es permanente y no se puede deshacer.
              </p>

              <div className="bg-red-50 dark:bg-red-900/10 p-4 rounded-xl mb-6 text-left border border-red-100 dark:border-red-900/30" data-id="element-4661">
                <label className="block text-sm font-medium text-red-800 dark:text-red-300 mb-2" data-id="element-4662">
                  Para confirmar, escribe <strong data-id="element-4663">ELIMINAR</strong>:
                </label>
                <input type="text" value={deleteAllConfirmText} onChange={e => setDeleteAllConfirmText(e.target.value)} placeholder="ELIMINAR" className="w-full px-3 py-2 border border-red-200 dark:border-red-800 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-white" data-id="element-4664" />
              </div>

              <div className="flex gap-3" data-id="element-4665">
                <Button variant="outline" onClick={() => {
              setShowDeleteAllModal(false);
              setDeleteAllConfirmText('');
            }} fullWidth disabled={isDeletingAll} data-id="element-4666">
                  Cancelar
                </Button>
                <Button onClick={handleDeleteAll} fullWidth disabled={deleteAllConfirmText !== 'ELIMINAR' || isDeletingAll} className="bg-red-600 hover:bg-red-700 dark:bg-red-700 dark:hover:bg-red-800" data-id="element-4667">
                  {isDeletingAll ? <span className="flex items-center gap-2" data-id="element-4668">
                      <Loader2 size={16} className="animate-spin" data-id="element-4669" />
                      Eliminando...
                    </span> : 'Eliminar Todos'}
                </Button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}